import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Clock,
  Euro,
  TrendingUp,
  AlertTriangle,
  Loader2,
  Download,
  ExternalLink,
  Mail,
  Check,
  XCircle,
} from 'lucide-react';
import { getDashboard, getImapPendientes, updateImapPendiente } from '../services/api';
import StatusBadge from '../components/StatusBadge';
import toast from 'react-hot-toast';

function StatCard({ icon: Icon, label, value, sublabel, color }) {
  return (
    <div className="card !p-3 sm:!p-6 flex flex-col sm:flex-row items-start gap-2 sm:gap-4">
      <div className={`p-2 sm:p-3 rounded-lg ${color}`}>
        <Icon className="w-4 h-4 sm:w-6 sm:h-6" />
      </div>
      <div className="min-w-0">
        <p className="text-xs sm:text-sm text-gray-500">{label}</p>
        <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{value}</p>
        {sublabel && <p className="text-xs text-gray-400 mt-1">{sublabel}</p>}
      </div>
    </div>
  );
}

function formatCurrency(value) {
  if (value === null || value === undefined) return '0,00 \u20ac';
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendientes, setPendientes] = useState([]);
  const [updatingPendiente, setUpdatingPendiente] = useState(null);
  const navigate = useNavigate();

  const loadPendientes = () => {
    getImapPendientes('pendiente')
      .then((d) => setPendientes(Array.isArray(d) ? d : []))
      .catch(() => {});
  };

  useEffect(() => {
    let cancelled = false;
    getDashboard()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) toast.error('Error cargando dashboard: ' + err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    loadPendientes();
    return () => { cancelled = true; };
  }, []);

  const handlePendienteAction = async (id, estado) => {
    setUpdatingPendiente(id);
    try {
      await updateImapPendiente(id, estado);
      setPendientes((prev) => prev.filter((p) => p.id !== id));
      toast.success(estado === 'descargada' ? 'Marcada como descargada' : 'Descartada');
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setUpdatingPendiente(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-gray-400">
        No se pudieron cargar los datos del dashboard
      </div>
    );
  }

  const stats = data;
  const topProveedores = data.por_proveedor_top5 || [];
  const ultimasFacturas = data.ultimas_facturas || [];
  const pendientesValidar = stats.pendientes_validar || 0;
  const errores = stats.errores || 0;

  return (
    <div className="space-y-6">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Panel de Control</h1>

      {/* Alert badges */}
      {(pendientesValidar > 0 || errores > 0) && (
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          {pendientesValidar > 0 && (
            <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-2 rounded-lg text-sm">
              <AlertTriangle className="w-4 h-4" />
              {pendientesValidar} factura{pendientesValidar !== 1 ? 's' : ''} pendiente{pendientesValidar !== 1 ? 's' : ''} de
              validar
            </div>
          )}
          {errores > 0 && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-800 px-4 py-2 rounded-lg text-sm">
              <AlertTriangle className="w-4 h-4" />
              {errores} factura{errores !== 1 ? 's' : ''} con error OCR
            </div>
          )}
        </div>
      )}

      {/* Pending downloads from IMAP */}
      {pendientes.length > 0 && (
        <div className="card !p-4 sm:!p-6">
          <div className="flex items-center gap-2 mb-4">
            <Download className="w-5 h-5 text-orange-500" />
            <h2 className="text-lg font-semibold text-gray-900">
              Facturas pendientes de descarga
            </h2>
            <span className="ml-auto bg-orange-100 text-orange-700 text-xs font-bold px-2 py-0.5 rounded-full">
              {pendientes.length}
            </span>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Estos emails contienen enlaces para descargar facturas. Descargalas manualmente y subilas desde Escanear.
          </p>
          <div className="space-y-3">
            {pendientes.map((p) => {
              const enlaces = Array.isArray(p.enlaces) ? p.enlaces : [];
              const fechaDisplay = p.imap_date
                ? new Date(p.imap_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
                : p.created_at?.split('T')[0] || '-';

              return (
                <div
                  key={p.id}
                  className="p-3 sm:p-4 bg-orange-50 border border-orange-200 rounded-lg space-y-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {p.asunto || '(sin asunto)'}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {p.imap_from || '-'}
                        </span>
                        <span>{fechaDisplay}</span>
                      </div>
                    </div>
                  </div>

                  {/* Download links */}
                  {enlaces.length > 0 && (
                    <div className="space-y-1">
                      {enlaces.slice(0, 5).map((url, i) => (
                        <a
                          key={i}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-800 hover:underline truncate"
                        >
                          <ExternalLink className="w-3 h-3 shrink-0" />
                          <span className="truncate">{url}</span>
                        </a>
                      ))}
                      {enlaces.length > 5 && (
                        <p className="text-xs text-gray-400">
                          +{enlaces.length - 5} enlace(s) mas
                        </p>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => handlePendienteAction(p.id, 'descargada')}
                      disabled={updatingPendiente === p.id}
                      className="flex items-center gap-1 text-xs text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded px-2 py-1 transition-colors"
                    >
                      <Check className="w-3 h-3" />
                      Ya descargada
                    </button>
                    <button
                      onClick={() => handlePendienteAction(p.id, 'descartada')}
                      disabled={updatingPendiente === p.id}
                      className="flex items-center gap-1 text-xs text-gray-500 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded px-2 py-1 transition-colors"
                    >
                      <XCircle className="w-3 h-3" />
                      Descartar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          icon={FileText}
          label="Total Facturas"
          value={stats.total_facturas || 0}
          color="bg-blue-100 text-blue-600"
        />
        <StatCard
          icon={Clock}
          label="Pendientes"
          value={pendientesValidar}
          color="bg-yellow-100 text-yellow-600"
        />
        <StatCard
          icon={Euro}
          label="Total Mes"
          value={formatCurrency(stats.total_mes_actual)}
          color="bg-green-100 text-green-600"
        />
        <StatCard
          icon={TrendingUp}
          label="Total Trimestre"
          value={formatCurrency(stats.total_trimestre)}
          color="bg-purple-100 text-purple-600"
        />
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Top proveedores */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Top 5 Proveedores
          </h2>
          {topProveedores.length > 0 ? (
            <div className="space-y-3">
              {topProveedores.slice(0, 5).map((prov, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                    <span className="text-sm text-gray-700 truncate">
                      {prov.nombre || prov.proveedor}
                    </span>
                  </div>
                  <span className="text-sm font-mono font-medium text-gray-900 whitespace-nowrap ml-2">
                    {formatCurrency(prov.total)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm">Sin datos</p>
          )}
        </div>
      </div>

      {/* Last invoices */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Últimas Facturas
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  Estado
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  Fecha
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  N Factura
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  Proveedor
                </th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(ultimasFacturas.length > 0 ? ultimasFacturas.slice(0, 5) : []).map(
                (factura) => (
                  <tr
                    key={factura.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/facturas/${factura.id}`)}
                  >
                    <td className="px-3 py-2">
                      <StatusBadge status={factura.estado} />
                    </td>
                    <td className="px-3 py-2 text-sm">{formatDate(factura.fecha_factura)}</td>
                    <td className="px-3 py-2 text-sm">{factura.numero_factura || '-'}</td>
                    <td className="px-3 py-2 text-sm">{factura.proveedor_nombre || '-'}</td>
                    <td className="px-3 py-2 text-sm font-mono text-right">
                      {formatCurrency(factura.total)}
                    </td>
                  </tr>
                )
              )}
              {ultimasFacturas.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-gray-400 text-sm">
                    No hay facturas registradas
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
